import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Container, Form, Button, Spinner, Alert, Row, Col, Card, Table, Badge,
} from 'react-bootstrap';
import {
  FaArrowLeft, FaTrash, FaPlus, FaEdit, FaExternalLinkAlt, FaSave,
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { api } from '../../../contexts/AuthContext';

const ACCENT = '#60A5FA';

const blankToNull = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

const emptyContact = () => ({
  first_name: '',
  last_name: '',
  position: '',
  phone_number: '',
  email: '',
});

const Company = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState(emptyContact());
  const [editingContactId, setEditingContactId] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const fetchCompany = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/companies/${id}/`);
      const c = res.data;
      setForm({
        name: c.name || '',
        description: c.description || '',
        address: c.address || '',
        city: c.city || '',
        postal_code: c.postal_code || '',
        country: c.country || '',
        phone_number: c.phone_number || c.phone || '',
        email: c.email || '',
        website: c.website || '',
        vat_number: c.vat_number || '',
        tax_office: c.tax_office || '',
      });
      setContacts(c.contact_persons || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load company.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        description: blankToNull(form.description),
        address: blankToNull(form.address),
        city: blankToNull(form.city),
        postal_code: blankToNull(form.postal_code),
        country: blankToNull(form.country),
        phone_number: blankToNull(form.phone_number),
        email: blankToNull(form.email),
        website: blankToNull(form.website),
        vat_number: blankToNull(form.vat_number),
        tax_office: blankToNull(form.tax_office),
      };
      await api.put(`/api/companies/${id}/`, payload);
      toast.success('Company saved');
      await fetchCompany();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.name?.[0] || 'Failed to save';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete “${form?.name}” and all contacts?`)) return;
    try {
      await api.delete(`/api/companies/${id}/`);
      toast.success('Deleted');
      navigate('/companies');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const startAddContact = () => {
    setEditingContactId(null);
    setContactForm(emptyContact());
    setShowContactForm(true);
  };

  const startEditContact = (contact) => {
    setEditingContactId(contact.id);
    setContactForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      position: contact.position || '',
      phone_number: contact.phone_number || '',
      email: contact.email || '',
    });
    setShowContactForm(true);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = {
      company: Number(id),
      first_name: contactForm.first_name.trim(),
      last_name: contactForm.last_name.trim(),
      position: contactForm.position.trim(),
      phone_number: blankToNull(contactForm.phone_number),
      email: blankToNull(contactForm.email),
    };
    try {
      if (editingContactId) {
        await api.put(`/api/contact-persons/${editingContactId}/`, payload);
        toast.success('Contact updated');
      } else {
        await api.post('/api/contact-persons/', payload);
        toast.success('Contact added');
      }
      setShowContactForm(false);
      setEditingContactId(null);
      setContactForm(emptyContact());
      await fetchCompany();
    } catch (err) {
      toast.error('Failed to save contact');
    }
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Remove ${contact.first_name} ${contact.last_name}?`)) return;
    try {
      await api.delete(`/api/contact-persons/${contact.id}/`);
      toast.success('Contact removed');
      await fetchCompany();
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error || 'Not found'}</Alert>
        <Link to="/companies" style={{ color: ACCENT }}>← Back to companies</Link>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <Link to="/companies" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 13 }}>
            <FaArrowLeft className="me-1" /> Companies
          </Link>
          <h2 style={{ color: ACCENT, marginTop: 6, marginBottom: 4 }}>{form.name || 'Company'}</h2>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            Edit details and manage people at this company
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-danger" onClick={handleDelete}>
            <FaTrash className="me-2" /> Delete
          </Button>
          <Button
            style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
            onClick={handleSave}
            disabled={saving}
          >
            <FaSave className="me-2" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Form onSubmit={handleSave}>
        <Row className="g-3 mb-4">
          <Col lg={8}>
            <Card className="bg-dark text-light border-secondary h-100">
              <Card.Header className="bg-dark border-secondary" style={{ color: ACCENT, fontWeight: 600 }}>
                Company details
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        required
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>VAT number</Form.Label>
                      <Form.Control
                        value={form.vat_number}
                        onChange={(e) => set('vat_number', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        value={form.address}
                        onChange={(e) => set('address', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        value={form.city}
                        onChange={(e) => set('city', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postal</Form.Label>
                      <Form.Control
                        value={form.postal_code}
                        onChange={(e) => set('postal_code', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        value={form.country}
                        onChange={(e) => set('country', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tax office</Form.Label>
                      <Form.Control
                        value={form.tax_office}
                        onChange={(e) => set('tax_office', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        value={form.phone_number}
                        onChange={(e) => set('phone_number', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={form.email}
                        onChange={(e) => set('email', e.target.value)}
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Website</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          value={form.website}
                          onChange={(e) => set('website', e.target.value)}
                          className="bg-dark text-light border-secondary"
                        />
                        {form.website ? (
                          <a
                            href={form.website.startsWith('http') ? form.website : `https://${form.website}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: ACCENT }}
                          >
                            <FaExternalLinkAlt />
                          </a>
                        ) : null}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="bg-dark text-light border-secondary h-100">
              <Card.Header className="bg-dark border-secondary d-flex justify-content-between align-items-center">
                <span style={{ color: ACCENT, fontWeight: 600 }}>
                  Contacts <Badge bg="secondary" className="ms-1">{contacts.length}</Badge>
                </span>
                <Button
                  size="sm"
                  style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
                  onClick={startAddContact}
                >
                  <FaPlus className="me-1" /> Add
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {showContactForm && (
                  <Form onSubmit={saveContact} className="p-3 border-bottom border-secondary">
                    <Row>
                      <Col xs={6}>
                        <Form.Control
                          required
                          placeholder="First"
                          value={contactForm.first_name}
                          onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                          className="bg-dark text-light border-secondary mb-2"
                          size="sm"
                        />
                      </Col>
                      <Col xs={6}>
                        <Form.Control
                          required
                          placeholder="Last"
                          value={contactForm.last_name}
                          onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                          className="bg-dark text-light border-secondary mb-2"
                          size="sm"
                        />
                      </Col>
                    </Row>
                    <Form.Control
                      placeholder="Position"
                      value={contactForm.position}
                      onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                      className="bg-dark text-light border-secondary mb-2"
                      size="sm"
                    />
                    <Form.Control
                      placeholder="Phone"
                      value={contactForm.phone_number}
                      onChange={(e) => setContactForm({ ...contactForm, phone_number: e.target.value })}
                      className="bg-dark text-light border-secondary mb-2"
                      size="sm"
                    />
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="bg-dark text-light border-secondary mb-2"
                      size="sm"
                    />
                    <div className="d-flex gap-2">
                      <Button type="submit" size="sm" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
                        {editingContactId ? 'Save' : 'Add'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setShowContactForm(false);
                          setEditingContactId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}

                {contacts.length === 0 && !showContactForm ? (
                  <div className="p-3 text-center" style={{ color: '#9ca3af' }}>
                    No contacts yet
                  </div>
                ) : (
                  <Table hover variant="dark" size="sm" className="mb-0 align-middle">
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>
                              {contact.first_name} {contact.last_name}
                            </div>
                            {contact.position ? (
                              <small style={{ color: '#9ca3af' }}>{contact.position}</small>
                            ) : null}
                            <div>
                              <small style={{ color: '#d1d5db' }}>
                                {[contact.phone_number, contact.email].filter(Boolean).join(' · ') || '—'}
                              </small>
                            </div>
                          </td>
                          <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-1"
                              style={{ borderColor: ACCENT, color: ACCENT }}
                              onClick={() => startEditContact(contact)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteContact(contact)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default Company;
